/**
 * The replay picker's tabs, as data.
 *
 * A researcher sorting trajectories into the three study conditions should never
 * have to open a log to learn which bucket it belongs in. The index row carries
 * the condition and who sat in each seat; these helpers turn that into a tab, a
 * count per tab, a label per chef, and a tab read back out of the address bar.
 * The batch — the invite code a round was recorded under — gets the same
 * treatment below, as a filter beside the tabs.
 *
 * Pure on purpose, so they are tested without a DOM, like `conversation.ts`.
 */

import type { SessionIndexEntry, StudyCondition } from '@overcooked/shared'

/**
 * A study condition, or the bucket for sessions nobody could classify — old
 * recordings whose log never said who played, and whose seats do not say either.
 */
export type TabCondition = StudyCondition | 'unlabelled'

/** The tabs, in the order the strip shows them. */
export const TABS: ReadonlyArray<{ condition: StudyCondition; label: string }> = [
  { condition: 'human-human', label: 'Human–Human' },
  { condition: 'ai-ai', label: 'AI–AI' },
  { condition: 'human-ai', label: 'Human–AI' },
]

/**
 * One person on their own. Not a study condition — there is no partner and no
 * recording — so it is shown after the three, and only when something is in it,
 * exactly like Unlabelled. Version-5 logs called these rounds `human-human`;
 * the server's index now files them here.
 */
export const SOLO_TAB: { condition: 'solo'; label: string } = { condition: 'solo', label: 'Solo' }

/** Shown after the three only when something is in it. */
export const UNLABELLED_TAB: { condition: 'unlabelled'; label: string } = {
  condition: 'unlabelled',
  label: 'Unlabelled',
}

export const DEFAULT_TAB: StudyCondition = 'human-human'

const CONDITIONS = new Set<string>([...TABS.map((tab) => tab.condition), SOLO_TAB.condition])

/**
 * The tab a session belongs in.
 *
 * `'unlabelled'` rather than a default: a session nobody classified must not
 * land in Human–Human by accident, which is exactly the confusion the tabs exist
 * to end.
 */
export function tabOf(entry: Pick<SessionIndexEntry, 'condition'>): TabCondition {
  const condition = entry.condition
  return condition && CONDITIONS.has(condition) ? condition : 'unlabelled'
}

/** How many sessions each tab holds. Every tab is present, zero or not. */
export function countByTab(
  sessions: ReadonlyArray<Pick<SessionIndexEntry, 'condition'>>,
): Record<TabCondition, number> {
  const counts: Record<TabCondition, number> = {
    'human-human': 0,
    'ai-ai': 0,
    'human-ai': 0,
    solo: 0,
    unlabelled: 0,
  }
  for (const session of sessions) counts[tabOf(session)] += 1
  return counts
}

/**
 * The tabs to draw: the three conditions always, Unlabelled only when it has
 * sessions — or when it is the tab the address bar asked for, so the selected
 * tab is never one the strip does not show.
 */
export function visibleTabs(
  counts: Record<TabCondition, number>,
  selected: TabCondition,
): ReadonlyArray<{ condition: TabCondition; label: string }> {
  const extra = [
    ...(counts.solo > 0 || selected === 'solo' ? [SOLO_TAB] : []),
    ...(counts.unlabelled > 0 || selected === 'unlabelled' ? [UNLABELLED_TAB] : []),
  ]
  return extra.length > 0 ? [...TABS, ...extra] : TABS
}

/**
 * What to call the chef in one seat.
 *
 * An agent is named by its model, because that is the variable the study is
 * about. A person is named by what they typed, or "Guest" if they typed nothing.
 */
export function seatLabel(seat: SessionIndexEntry['seats'][number]): string {
  if (seat.kind === 'agent') return seat.model ?? seat.name ?? 'AI chef'
  if (seat.kind === 'human') return seat.name ?? 'Guest'
  return seat.name ?? 'Unknown'
}

/** `?condition=ai-ai` → `'ai-ai'`; anything missing or unrecognised → the default tab. */
export function parseConditionQuery(search: string): TabCondition {
  const value = new URLSearchParams(search).get('condition')
  if (value === UNLABELLED_TAB.condition) return value
  return value && CONDITIONS.has(value) ? (value as StudyCondition) : DEFAULT_TAB
}

// ---------------------------------------------------------------------------
// Batches — the invite code a session was recorded under
//
// A second, independent filter beside the condition tabs rather than a tab
// dimension of its own: the tabs stay exactly what they were, and a link with
// no `?invite=` behaves as it always did.
//
// The filter value has three shapes, the same three the server reads:
//
//   undefined   every batch — the parameter is absent
//   ''          "No batch" — rounds from rooms opened without a code, and every
//               round recorded before codes existed
//   'PILOT-A'   one batch
// ---------------------------------------------------------------------------

/** What a session with no invite code is called, everywhere it is shown. */
export const NO_BATCH_LABEL = 'No batch'

/** The value of the "No batch" choice, which is also what `?invite=` carries for it. */
export const NO_BATCH = ''

/** A batch filter: `undefined` for all, `NO_BATCH`, or a code. */
export type BatchFilter = string | undefined

/**
 * What to call the batch a session belongs to.
 *
 * `?? 'No batch'` covers both `null` (a room opened with the check off) and a
 * missing field (an index row written before the field existed): neither says
 * a batch, and neither is given one.
 */
export function batchLabel(entry: { invite?: string | null }): string {
  return entry.invite ?? NO_BATCH_LABEL
}

/**
 * `?invite=pilot-a` → `'PILOT-A'`; `?invite=` → `''` (No batch); absent → `undefined`.
 *
 * Upper-cased and trimmed the way a code is typed on the landing page, so a
 * link somebody wrote by hand lands on the same batch. Anything else is passed
 * on as written and the server answers with an empty list, which is the honest
 * answer to a batch that does not exist.
 */
export function parseInviteQuery(search: string): BatchFilter {
  const value = new URLSearchParams(search).get('invite')
  if (value === null) return undefined
  return value.trim().toUpperCase()
}

/** The query-string pair for a filter, or `null` when there is nothing to say. */
export function inviteParam(invite: BatchFilter): string | null {
  return invite === undefined ? null : `invite=${encodeURIComponent(invite)}`
}

/**
 * The replay page's query string: the condition tab always, the batch when one
 * is chosen. `pushState` writes both so a link keeps both, and the back button
 * restores both.
 */
export function replaySearch(condition: TabCondition, invite: BatchFilter): string {
  const batch = inviteParam(invite)
  return `?condition=${condition}${batch ? `&${batch}` : ''}`
}

/**
 * The codes to offer in the batch picker, sorted, from the sessions seen so far
 * — plus the selected one, so the picker never hides the filter it is showing.
 * "All batches" and "No batch" are the picker's own and are not listed here.
 */
export function batchOptions(
  sessions: ReadonlyArray<{ invite?: string | null }>,
  selected: BatchFilter,
): string[] {
  const codes = new Set<string>()
  for (const session of sessions) if (session.invite) codes.add(session.invite)
  if (selected) codes.add(selected)
  return [...codes].sort()
}

/**
 * Fold the batches a freshly loaded list names into the ones already known.
 *
 * Hands back `known` itself when nothing is new, so a React state setter that
 * receives it does not re-render for a list that taught it nothing.
 */
export function rememberBatches(
  known: readonly string[],
  sessions: ReadonlyArray<{ invite?: string | null }>,
): readonly string[] {
  const learnt = sessions.some((session) => Boolean(session.invite) && !known.includes(session.invite!))
  return learnt ? batchOptions([...known.map((invite) => ({ invite })), ...sessions], undefined) : known
}
